// CraftImageLab — parent-facing curation sheet for the global
// emoji_combinations image cache. Renders every cached combination
// with current image (or placeholder when none), lets the parent
// trigger a regenerate per row, and pick from a few preset prompt
// styles to evaluate before locking in the look. Edge function
// kid-craft-regen-image does the actual gen.
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**)
// is the explicit opt-in to emojis.

import { useCallback, useEffect, useState } from "react";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";
import { supabase } from "../../lib/supabase";

const STYLES = [
  { id: "emoji",     label: "Emoji"     },
  { id: "sticker",   label: "Sticker"   },
  { id: "watercolor",label: "Watercolor"},
  { id: "claymation",label: "Claymation"},
  { id: "pixar",     label: "Pixar 3D"  },
  { id: "flat",      label: "Flat"      },
];

export function CraftImageLab({ onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [style, setStyle] = useState("emoji");
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all"); // all | missing | ready
  const [editingRow, setEditingRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("emoji_combinations")
      .select("a_emoji, b_emoji, result_name, result_emoji, image_url, image_status, first_discovered_at")
      .order("first_discovered_at", { ascending: false })
      .limit(400);
    if (error) {
      console.warn("[craft-lab] load:", error.message);
      setError(error.message);
    }
    setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Realtime — when a regen completes, the row's image_url +
  // image_status update; the lab grid swaps in the new image.
  useEffect(() => {
    const ch = supabase.channel("craft-lab")
      .on("postgres_changes",
        { event: "UPDATE", schema: "public", table: "emoji_combinations" },
        () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [load]);

  const regen = async (row) => {
    const key = `${row.a_emoji}|${row.b_emoji}`;
    setBusyKey(key);
    setError(null);
    const { data, error } = await supabase.functions.invoke("kid-craft-regen-image", {
      body: {
        a_emoji:      row.a_emoji,
        b_emoji:      row.b_emoji,
        prompt_style: style,
      },
    });
    setBusyKey(null);
    if (error || data?.error) {
      const msg = error?.message || data?.error || "regen failed";
      setError(msg);
    }
  };

  const deleteRow = async (row) => {
    if (!confirm(`Delete "${row.result_name}"? It'll be re-generated next time someone combines those items.`)) return;
    const key = `${row.a_emoji}|${row.b_emoji}`;
    setBusyKey(key);
    setError(null);
    const { error } = await supabase.rpc("delete_emoji_combination", {
      p_ingredients_key: (row.ingredients_key || (row.a_emoji + row.b_emoji)),
      p_c_emoji: row.c_emoji || "",
    });
    setBusyKey(null);
    if (error) setError(error.message);
    else load();
  };

  // REMOVED: resetAll. Wiping every kid-discovered combo nuked the
  // GLOBAL cache for all users — a one-click footgun. Per-kid resets
  // belong in KidParentPanel via reset_kid_craft RPC, which only
  // touches that kid's kid_craft_inventory (never the cache).
  //
  // If an admin truly needs to bulk-clear cached AI generations, do
  // it from the SQL editor with an explicit WHERE clause — the
  // friction is the safety.

  const filteredRows = (() => {
    if (filter === "missing") return rows.filter((r) => !r.image_url);
    if (filter === "ready")   return rows.filter((r) => !!r.image_url);
    return rows;
  })();

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(30,30,30,0.5)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 220,
        padding: 0,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 560,
          height: "92vh",
          background: "#FAF6EE",
          borderRadius: "20px 20px 0 0",
          padding: "20px 0 calc(16px + env(safe-area-inset-bottom))",
          boxShadow: "0 -8px 28px rgba(0,0,0,0.22)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ padding: "0 20px 12px", flexShrink: 0 }}>
          <div
            style={{
              fontFamily: FONT.itemTitle,
              fontStyle: "italic",
              fontSize: 22,
              color: COLOR.ink,
              marginBottom: 4,
            }}
          >
            Recipe Lab
          </div>
          <div
            style={{
              fontFamily: FONT.forma,
              fontSize: 12,
              color: COLOR.muted,
              marginBottom: 12,
              lineHeight: 1.45,
            }}
          >
            Pick a style, then tap any tile to regenerate its image.
            ~$0.003 per regen. Realtime — image swaps in when it lands.
          </div>

          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontFamily: FONT.mono,
                fontSize: 10,
                letterSpacing: "0.1em",
                fontWeight: 700,
                color: COLOR.sub,
                marginBottom: 6,
              }}
            >
              STYLE
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {STYLES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStyle(s.id)}
                  style={{
                    padding: "8px 14px",
                    background: style === s.id ? COLOR.leaf : "#fff",
                    color:      style === s.id ? "#fff"     : COLOR.ink,
                    border: `1px solid ${style === s.id ? COLOR.leaf : COLOR.border}`,
                    borderRadius: 999,
                    fontFamily: FONT.mono,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    cursor: "pointer",
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {["all", "missing", "ready"].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                style={{
                  padding: "6px 12px",
                  background: filter === f ? COLOR.skyDeep : "transparent",
                  color: filter === f ? COLOR.sky : COLOR.muted,
                  border: `1px solid ${filter === f ? COLOR.skyBorder : COLOR.border}`,
                  borderRadius: 999,
                  fontFamily: FONT.mono,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.08em",
                  cursor: "pointer",
                }}
              >
                {f.toUpperCase()}
              </button>
            ))}
            {/* Global-cache reset button removed — per-kid resets
                live in KidParentPanel (reset_kid_craft RPC). */}
          </div>

          {error && (
            <div
              style={{
                marginTop: 10,
                padding: "8px 12px",
                background: COLOR.roseDeep,
                border: `1px solid ${COLOR.roseDim}`,
                borderRadius: 8,
                color: COLOR.rose,
                fontFamily: FONT.forma,
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            padding: "4px 16px 16px",
          }}
        >
          {loading ? (
            <div style={{ padding: 20, textAlign: "center", color: COLOR.muted, fontFamily: FONT.forma, fontSize: 13 }}>
              Loading…
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                gap: 10,
              }}
            >
              {filteredRows.map((r) => {
                const key = `${r.a_emoji}|${r.b_emoji}`;
                const busy = busyKey === key || r.image_status === "pending";
                return (
                  <ComboTile
                    key={key}
                    row={r}
                    busy={busy}
                    onRegen={() => regen(r)}
                    onEdit={() => setEditingRow(r)}
                    onDelete={() => deleteRow(r)}
                  />
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding: "12px 20px 0", flexShrink: 0 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: "100%",
              padding: 14,
              background: COLOR.leaf,
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: "pointer",
            }}
          >
            DONE
          </button>
        </div>
      </div>

      {editingRow && (
        <EditNameSheet
          row={editingRow}
          style={style}
          onClose={() => setEditingRow(null)}
          onSaved={() => { setEditingRow(null); load(); }}
        />
      )}
    </div>
  );
}

function EditNameSheet({ row, style, onClose, onSaved }) {
  const [name, setName] = useState(row.result_name || "");
  const [emoji, setEmoji] = useState(row.result_emoji || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const save = async ({ andRegen = false } = {}) => {
    const trimmedName = name.trim().slice(0, 60);
    const trimmedEmoji = emoji.trim().slice(0, 4);
    if (!trimmedName || !trimmedEmoji) {
      setErr("name and emoji required");
      return;
    }
    setBusy(true);
    setErr(null);
    const { error } = await supabase
      .from("emoji_combinations")
      .update({ result_name: trimmedName, result_emoji: trimmedEmoji })
      .eq("a_emoji", row.a_emoji)
      .eq("b_emoji", row.b_emoji);
    if (error) {
      setBusy(false);
      setErr(error.message);
      return;
    }
    if (andRegen) {
      const { error: e2 } = await supabase.functions.invoke("kid-craft-regen-image", {
        body: {
          a_emoji:      row.a_emoji,
          b_emoji:      row.b_emoji,
          prompt_style: style,
        },
      });
      if (e2) {
        setBusy(false);
        setErr(e2.message || "regen failed");
        return;
      }
    }
    setBusy(false);
    onSaved();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,18,14,0.55)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 260,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          background: "#FAF6EE",
          borderRadius: 20,
          padding: "20px 20px calc(20px + env(safe-area-inset-bottom))",
          boxShadow: "0 -8px 24px rgba(0,0,0,0.22)",
        }}
      >
        <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 22, color: COLOR.ink, marginBottom: 4 }}>
          Edit recipe
        </div>
        <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.muted, marginBottom: 14 }}>
          {row.a_emoji} + {row.b_emoji}
        </div>

        <label style={{ display: "block", marginBottom: 10 }}>
          <div style={{ fontFamily: FONT.mono, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, color: COLOR.sub, marginBottom: 4 }}>
            NAME
          </div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            autoFocus
            style={{
              width: "100%",
              padding: "12px 14px",
              fontFamily: "inherit",
              fontSize: 16,
              background: "#fff",
              border: `1px solid ${COLOR.border}`,
              borderRadius: 10,
              boxSizing: "border-box",
              color: COLOR.ink,
            }}
          />
        </label>

        <label style={{ display: "block", marginBottom: 14 }}>
          <div style={{ fontFamily: FONT.mono, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, color: COLOR.sub, marginBottom: 4 }}>
            FALLBACK EMOJI
          </div>
          <input
            type="text"
            value={emoji}
            onChange={(e) => setEmoji(e.target.value)}
            maxLength={4}
            style={{
              width: "100%",
              padding: "12px 14px",
              fontFamily: "inherit",
              fontSize: 22,
              textAlign: "center",
              background: "#fff",
              border: `1px solid ${COLOR.border}`,
              borderRadius: 10,
              boxSizing: "border-box",
              color: COLOR.ink,
            }}
          />
        </label>

        {err && (
          <div
            style={{
              marginBottom: 10,
              padding: "8px 12px",
              background: COLOR.roseDeep,
              border: `1px solid ${COLOR.roseDim}`,
              borderRadius: 8,
              color: COLOR.rose,
              fontFamily: FONT.forma,
              fontSize: 12,
            }}
          >
            {err}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              flex: 1,
              padding: 14,
              background: "transparent",
              color: COLOR.muted,
              border: `1px solid ${COLOR.border}`,
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: "pointer",
            }}
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={() => save({ andRegen: false })}
            disabled={busy}
            style={{
              flex: 2,
              padding: 14,
              background: COLOR.leaf,
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: busy ? "wait" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? "SAVING…" : "SAVE NAME"}
          </button>
        </div>
        <button
          type="button"
          onClick={() => save({ andRegen: true })}
          disabled={busy}
          style={{
            width: "100%",
            padding: 12,
            background: COLOR.skyDeep,
            color: COLOR.sky,
            border: `1px solid ${COLOR.skyBorder}`,
            borderRadius: 10,
            fontFamily: FONT.mono,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.08em",
            cursor: busy ? "wait" : "pointer",
            opacity: busy ? 0.6 : 1,
          }}
        >
          ✨ SAVE & REGEN IMAGE
        </button>
      </div>
    </div>
  );
}

function ComboTile({ row, busy, onRegen, onEdit, onDelete }) {
  const hasImage = !!row.image_url;
  return (
    <div
      style={{
        position: "relative",
        background: hasImage ? `url(${row.image_url}) center / cover no-repeat` : "#fff",
        border: `1px solid ${row.image_status === "failed" ? COLOR.roseDim : COLOR.border}`,
        borderRadius: RADIUS.xxl,
        overflow: "hidden",
        aspectRatio: "1 / 1",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      <button
        type="button"
        onClick={onRegen}
        disabled={busy}
        aria-label="regenerate image"
        style={{
          all: "unset",
          cursor: busy ? "wait" : "pointer",
          position: "absolute",
          inset: 0,
          touchAction: "manipulation",
        }}
      />
      {!hasImage && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 56,
            opacity: 0.7,
          }}
        >
          {row.result_emoji}
        </div>
      )}
      {busy && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontSize: 28,
              display: "inline-block",
              animation: "lab-spin 1.4s linear infinite",
            }}
          >
            ✨
          </span>
          <style>{`@keyframes lab-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          padding: "16px 8px 8px",
          background: hasImage
            ? "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%)"
            : "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 100%)",
        }}
      >
        <div
          style={{
            fontFamily: FONT.forma,
            fontSize: 11,
            fontWeight: 700,
            color: hasImage ? "#fff" : COLOR.ink,
            lineHeight: 1.15,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textShadow: hasImage ? "0 1px 2px rgba(0,0,0,0.6)" : "none",
          }}
        >
          {row.result_name}
        </div>
        <div
          style={{
            fontFamily: FONT.mono,
            fontSize: 9,
            letterSpacing: "0.06em",
            color: hasImage ? "rgba(255,255,255,0.85)" : COLOR.sub,
            marginTop: 2,
          }}
        >
          {row.a_emoji} + {row.b_emoji}
        </div>
      </div>

      {/* Top-right corner actions — edit + delete. Both stop
          propagation so the full-tile regen click doesn't fire. */}
      <div
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          display: "flex",
          gap: 4,
          zIndex: 5,
        }}
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
          aria-label="edit name"
          style={{
            width: 28,
            height: 28,
            padding: 0,
            background: "rgba(255,255,255,0.92)",
            border: `1px solid ${COLOR.border}`,
            borderRadius: 999,
            fontSize: 13,
            cursor: "pointer",
            touchAction: "manipulation",
            boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
          }}
        >
          ✏️
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
          aria-label="delete combo"
          style={{
            width: 28,
            height: 28,
            padding: 0,
            background: "rgba(255,255,255,0.92)",
            border: `1px solid ${COLOR.roseDim}`,
            borderRadius: 999,
            fontSize: 13,
            cursor: "pointer",
            touchAction: "manipulation",
            boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
            color: COLOR.rose,
          }}
        >
          🗑️
        </button>
      </div>
    </div>
  );
}

export default CraftImageLab;
